import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance WITHOUT withCredentials to avoid cross-origin cookie issues
// We'll rely solely on token-based auth via Authorization header
const api = axios.create({
    baseURL: API_URL,
    withCredentials: false, // Disabled to avoid Safari/Firefox cross-origin cookie issues
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
    try {
        const token = localStorage.getItem('session_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            // Also add as X-Session-Token for backwards compatibility
            config.headers['X-Session-Token'] = token;
        }
    } catch (e) {
        console.warn('Could not access localStorage:', e);
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Handle responses and store token if returned
api.interceptors.response.use(
    (response) => {
        try {
            // Check if response has a token in data (most reliable method for cross-browser)
            const tokenFromData = response.data?.session_token;
            // Also check headers as backup (lowercase for axios compatibility)
            const tokenFromHeader = response.headers?.['x-session-token'] || 
                                   response.headers?.['X-Session-Token'];
            
            const token = tokenFromData || tokenFromHeader;
            
            if (token) {
                localStorage.setItem('session_token', token);
            }
        } catch (e) {
            console.warn('Could not store session token:', e);
        }
        return response;
    },
    (error) => {
        // Handle 401 Unauthorized responses
        if (error.response?.status === 401) {
            try {
                const authError = error.response?.headers?.['x-auth-error'];
                const errorDetail = error.response?.data?.detail || '';
                
                // Only clear token for genuine session errors, not general auth failures
                const isSessionError = authError === 'session_not_found' || 
                                       authError === 'session_expired' ||
                                       errorDetail.includes('Session not found') || 
                                       errorDetail.includes('Session expired') ||
                                       errorDetail.includes('Not authenticated');
                
                if (isSessionError) {
                    console.log('Session invalid, clearing auth state');
                    localStorage.removeItem('session_token');
                    window.dispatchEvent(new CustomEvent('auth-session-invalid'));
                }
            } catch (e) {
                console.warn('Could not clear session token:', e);
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    processSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
    getMe: () => api.get('/auth/me'),
    logout: () => {
        localStorage.removeItem('session_token');
        return api.post('/auth/logout');
    },
};

// User API
export const userAPI = {
    updateProfile: (data) => api.put('/users/me', data),
    getDashboard: () => api.get('/users/dashboard'),
    changePassword: (data) => api.post('/auth/change-password', data),
    getApplicationProgress: () => api.get('/users/application-progress'),
    cancelSubscription: () => api.post('/users/cancel-subscription'),
    trackView: (type, itemId) => api.post('/users/track-view', { type, item_id: itemId }),
    getViewedAgencies: () => api.get('/users/viewed-agencies'),
    getViewedJobs: () => api.get('/users/viewed-jobs'),
    getOptimisedCV: () => api.get('/users/optimised-cv'),
    downloadOptimisedCV: () => api.get('/users/optimised-cv/download', { responseType: 'blob' }),
    getNotifications: () => api.get('/users/notifications'),
    markNotificationRead: (id) => api.post(`/users/notifications/${id}/read`),
    markAllNotificationsRead: () => api.post('/users/notifications/read-all'),
    uploadCV: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/users/upload-cv', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    deleteCV: () => api.delete('/users/cv'),
    uploadProfilePicture: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/users/profile-picture', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    deleteProfilePicture: () => api.delete('/users/profile-picture'),
};

// Agency API
export const agencyAPI = {
    list: (params) => api.get('/agencies', { params }),
    getById: (agencyId) => api.get(`/agencies/${agencyId}`),
    submitCV: (agencyId, data) => api.post(`/agencies/${agencyId}/submit-cv`, data),
    cvStatus: (agencyId) => api.get(`/agencies/${agencyId}/cv-status`),
};

export const upsellAPI = {
    track: (event) => api.post('/upsell/event', { event }),
};

// Admin Agency API
export const adminAgencyAPI = {
    list: (params) => api.get('/admin/agencies', { params }),
    create: (data) => api.post('/admin/agencies', data),
    update: (agencyId, data) => api.put(`/admin/agencies/${agencyId}`, data),
    delete: (agencyId) => api.delete(`/admin/agencies/${agencyId}`),
    bulkDelete: (agencyIds) => api.post('/admin/agencies/bulk-delete', { agency_ids: agencyIds }),
    getCVSubmissions: (params) => api.get('/admin/cv-submissions', { params }),
    getMultiApplyOrders: (params) => api.get('/admin/multi-apply-orders', { params }),
    updateMultiApplyStatus: (orderId, status) => api.put(`/admin/multi-apply-orders/${orderId}/status`, { status }),
    updateMultiApplyCredits: (orderId, data) => api.put(`/admin/multi-apply-orders/${orderId}/credits`, data),
    uploadOptimisedCV: (userId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/admin/users/${userId}/optimised-cv`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    sendNotification: (data) => api.post('/admin/notifications/send', data),
    listUsersSimple: (search) => api.get('/admin/users/list', { params: { search } }),
    getLiveVisitors: () => api.get('/admin/live-visitors'),
    getPaidSubscribers: (params) => api.get('/admin/paid-subscribers', { params }),
};

// Payment API
export const paymentAPI = {
    createSubscriptionCheckout: (tier, originUrl) => 
        api.post('/payments/subscription/checkout', { tier, origin_url: originUrl }),
    createMultiApplyCheckout: (packageName, originUrl, jobPreferences) => 
        api.post('/payments/multi-apply/checkout', { 
            package: packageName, 
            origin_url: originUrl,
            job_preferences: jobPreferences 
        }),
    getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
    // Custom checkout endpoints
    createPaymentIntent: (data) => api.post('/payments/create-intent', data),
    confirmPayment: (paymentIntentId, extras = {}) => api.post('/payments/confirm', { payment_intent_id: paymentIntentId, ...extras }),
};

// Multi-Apply API
export const multiApplyAPI = {
    getOrders: () => api.get('/multi-apply/orders'),
    getOrder: (orderId) => api.get(`/multi-apply/orders/${orderId}`),
    uploadCV: (orderId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/multi-apply/orders/${orderId}/upload-cv`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Applications API
export const applicationsAPI = {
    list: (orderId) => api.get('/applications', { params: { order_id: orderId } }),
    create: (data) => api.post('/admin/applications', data),
    update: (applicationId, data) => api.put(`/admin/applications/${applicationId}`, data),
};

// Consultation API
export const consultationAPI = {
    create: (data) => api.post('/consultations', data),
    list: () => api.get('/consultations'),
    getAvailableSlots: (date) => api.get('/consultations/available-slots', { params: { date } }),
    adminList: (params) => api.get('/admin/consultations', { params }),
};

// Reviews API
export const reviewsAPI = {
    create: (data) => api.post('/reviews', data),
    getAgencyReviews: (agencyId, params) => api.get(`/reviews/agency/${agencyId}`, { params }),
    getUserReviews: () => api.get('/reviews/user'),
    delete: (reviewId) => api.delete(`/reviews/${reviewId}`),
};

// Super Admin API
export const superAdminAPI = {
    getUsers: (params) => api.get('/super-admin/users', { params }),
    updateUserRole: (userId, role) => api.put(`/super-admin/users/${userId}/role`, { role }),
    getStats: () => api.get('/super-admin/stats'),
    getOrders: (params) => api.get('/super-admin/multi-apply-orders', { params }),
    createAdmin: (data) => api.post('/super-admin/create-admin', data),
    listAdmins: () => api.get('/super-admin/admins'),
    deleteAdmin: (userId) => api.delete(`/super-admin/admins/${userId}`),
    updateAdmin: (userId, data) => api.put(`/super-admin/admins/${userId}`, data),
    updateAdminPermissions: (userId, permissions) => api.put(`/super-admin/admins/${userId}/permissions`, { permissions }),
    // Staff management
    createStaff: (data) => api.post('/super-admin/create-staff', data),
    listStaff: () => api.get('/super-admin/staff'),
    updateStaff: (userId, data) => api.put(`/super-admin/staff/${userId}`, data),
    deleteStaff: (userId) => api.delete(`/super-admin/staff/${userId}`),
};

// Staff API (multi-apply only)
export const staffAPI = {
    listOrders: (params) => api.get('/staff/multi-apply-orders', { params }),
    getOrder: (orderId) => api.get(`/staff/multi-apply-orders/${orderId}`),
    updateStatus: (orderId, status) => api.put(`/staff/multi-apply-orders/${orderId}/status`, { status }),
    updateNotes: (orderId, notes) => api.put(`/staff/multi-apply-orders/${orderId}/notes`, { notes }),
    updateCredits: (orderId, applications_sent) => api.put(`/staff/multi-apply-orders/${orderId}/credits`, { applications_sent }),
};

// Site Settings API
export const settingsAPI = {
    getPublic: () => api.get('/settings/public'),
    updateWhatsApp: (data) => api.put('/admin/settings/whatsapp', data),
};

// Course Inquiries API
export const courseInquiryAPI = {
    submit: (data) => api.post('/course-inquiries', data),
    adminList: (params) => api.get('/admin/course-inquiries', { params }),
    adminUpdate: (inquiryId, data) => api.put(`/admin/course-inquiries/${inquiryId}`, data),
    adminDelete: (inquiryId) => api.delete(`/admin/course-inquiries/${inquiryId}`),
};

// Blog API (Public)
export const blogAPI = {
    getPosts: (params) => api.get('/blog/posts', { params }),
    getPost: (slug) => api.get(`/blog/posts/${slug}`),
    getCategories: () => api.get('/blog/categories'),
};

// Admin Blog API
export const adminBlogAPI = {
    getPosts: (params) => api.get('/admin/blog/posts', { params }),
    getPost: (postId) => api.get(`/admin/blog/posts/${postId}`),
    create: (data) => api.post('/admin/blog/posts', data),
    update: (postId, data) => api.put(`/admin/blog/posts/${postId}`, data),
    delete: (postId) => api.delete(`/admin/blog/posts/${postId}`),
    importGoogleDoc: (data) => api.post('/admin/blog/import-google-doc', data),
};

// Testimonials API (Public)
export const testimonialsAPI = {
    list: () => api.get('/testimonials'),
};

// Admin Testimonials API
export const adminTestimonialsAPI = {
    list: () => api.get('/admin/testimonials'),
    create: (data) => api.post('/admin/testimonials', data),
    update: (testimonialId, data) => api.put(`/admin/testimonials/${testimonialId}`, data),
    delete: (testimonialId) => api.delete(`/admin/testimonials/${testimonialId}`),
    uploadImage: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/admin/upload/testimonial-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Jobs API (Public)
export const jobsAPI = {
    list: (params) => api.get('/jobs', { params }),
    get: (jobId) => api.get(`/jobs/${jobId}`),
    getByAgency: (agencyId) => api.get(`/agencies/${agencyId}/jobs`),
    search: (params) => api.get('/jobs/search', { params }),
};

// Admin Jobs API
export const adminJobsAPI = {
    list: (params) => api.get('/admin/jobs', { params }),
    create: (data) => api.post('/admin/jobs', data),
    update: (jobId, data) => api.put(`/admin/jobs/${jobId}`, data),
    updateStatus: (jobId, status) => api.put(`/admin/jobs/${jobId}/status`, { status }),
    delete: (jobId) => api.delete(`/admin/jobs/${jobId}`),
    bulkDelete: (jobIds) => api.post('/admin/jobs/bulk-delete', { job_ids: jobIds }),
    scrape: (url) => api.post('/admin/scrape/jobs', { url }),
    importJobs: (agencyId, jobs, country = 'UK') => {
        const params = new URLSearchParams();
        if (agencyId) params.append('agency_id', agencyId);
        if (country) params.append('country', country);
        const query = params.toString();
        const url = query ? `/admin/scrape/import?${query}` : '/admin/scrape/import';
        return api.post(url, jobs);
    },
};

// Admin Agency Scraper API
export const adminAgencyScraperAPI = {
    scrape: (url) => api.post('/admin/scrape/agencies', { url }),
    importAgencies: (agencies) => api.post('/admin/scrape/agencies/import', agencies),
};

export default api;
